import { AsyncPipe, DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { map } from 'rxjs'
import { AtlasEnvironment } from './environments'
import { ConnectionState, EnvironmentStatus, EnvironmentStatusService } from './environment-status.service'

const STATUS_LABELS: Record<ConnectionState, string> = {
  checking: 'Checking',
  connected: 'Connected',
  signInRequired: 'Sign-in expired',
  notPermitted: 'Not permitted',
  unreachable: 'Unreachable',
}

const DIRECTIONS: Record<ConnectionState, string> = {
  checking: 'Reading this environment.',
  connected: '',
  signInRequired: 'Your session here has expired.',
  notPermitted: 'This account is not a support user here.',
  unreachable: 'No response from this environment.',
}

type EnvironmentRow = EnvironmentStatus & { versionDrift: boolean }

// The version most connected environments agree on. Anything else is a rollout that stalled or a
// hotfix that never made it past one customer, which is the whole point of aggregating.
function prevailingVersion(statuses: EnvironmentStatus[]): string | null {
  const tally = new Map<string, number>()
  for (const status of statuses) {
    const version = status.overview?.version
    if (typeof version === 'string') {
      tally.set(version, (tally.get(version) ?? 0) + 1)
    }
  }
  const ranked = [...tally.entries()].sort((left, right) => right[1] - left[1])
  return ranked.length === 0 ? null : ranked[0][0]
}

function rollCall(statuses: EnvironmentStatus[]): string {
  const tally = (state: ConnectionState) => statuses.filter((status) => status.state === state).length
  const connected = tally('connected')
  if (connected === statuses.length) {
    return `All ${statuses.length} environments connected.`
  }
  const segments = [`${connected} of ${statuses.length} connected`]
  const trailing: [ConnectionState, (count: number) => string][] = [
    ['signInRequired', (count) => `${count} ${count === 1 ? 'needs' : 'need'} sign-in`],
    ['unreachable', (count) => `${count} unreachable`],
    ['notPermitted', (count) => `${count} not permitted`],
    ['checking', (count) => `${count} still checking`],
  ]
  for (const [state, wording] of trailing) {
    const count = tally(state)
    if (count > 0) {
      segments.push(wording(count))
    }
  }
  return segments.join(' · ')
}

@Component({
  selector: 'atlas-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DatePipe],
})
export class AppComponent {
  private service = inject(EnvironmentStatusService)

  readonly vm$ = this.service.statuses$.pipe(
    map((statuses) => {
      const prevailing = prevailingVersion(statuses)
      const checkedStamps = statuses.map((status) => status.checkedAt).filter((checkedAt): checkedAt is Date => checkedAt !== null)
      const rows: EnvironmentRow[] = statuses.map((status) => ({
        ...status,
        versionDrift: prevailing !== null && status.overview?.version != null && status.overview.version !== prevailing,
      }))
      return {
        rows,
        rollCall: rollCall(statuses),
        isSweeping: statuses.some((status) => status.state === 'checking'),
        lastCheckedAt: checkedStamps.length === 0 ? null : new Date(Math.max(...checkedStamps.map((stamp) => stamp.getTime()))),
      }
    })
  )

  statusLabel(state: ConnectionState): string {
    return STATUS_LABELS[state]
  }

  direction(state: ConnectionState): string {
    return DIRECTIONS[state]
  }

  shortVersion(version: string | null): string {
    return version === null ? '—' : version.slice(0, 7)
  }

  signIn(environment: AtlasEnvironment): void {
    window.open(`https://${environment.host}/app`, '_blank', 'noopener')
  }

  retry(environment: AtlasEnvironment): void {
    this.service.refresh(environment)
  }

  refreshAll(): void {
    this.service.refreshAll()
  }
}
